import { CreatePublicHolidayDto } from "../api-client";
import moment from "moment";

/**
 * Parsed event from iCal file
 */
export interface ParsedICalEvent {
  title: string;
  start: string;
  end: string;
  description?: string;
}

/**
 * Parses an iCal (.ics) file content and extracts VEVENT entries
 * @param icalContent - The raw iCal file content as string
 * @returns Array of parsed events
 */
export function parseICalContent(icalContent: string): ParsedICalEvent[] {
  const events: ParsedICalEvent[] = [];
  
  // Split into lines and normalize line endings
  const lines = icalContent
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');

  let currentEvent: Partial<ParsedICalEvent> | null = null;
  let inEvent = false;
  // DTEND of an all-day event is exclusive (RFC 5545) and must be converted
  // to an inclusive last day once DTSTART is known.
  let endIsExclusive = false;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    
    // Handle line continuation (lines starting with space or tab)
    while (i + 1 < lines.length && (lines[i + 1].startsWith(' ') || lines[i + 1].startsWith('\t'))) {
      i++;
      line += lines[i].substring(1);
    }

    if (line.startsWith('BEGIN:VEVENT')) {
      inEvent = true;
      currentEvent = {};
      endIsExclusive = false;
    } else if (line.startsWith('END:VEVENT') && currentEvent) {
      inEvent = false;
      if (currentEvent.title && currentEvent.start) {
        // If no end date, set to same as start (all-day event)
        if (!currentEvent.end) {
          currentEvent.end = currentEvent.start;
        } else if (endIsExclusive) {
          const inclusiveEnd = moment(currentEvent.end, 'YYYY-MM-DD').subtract(1, 'day');
          currentEvent.end = inclusiveEnd.isBefore(moment(currentEvent.start, 'YYYY-MM-DD'))
            ? currentEvent.start
            : inclusiveEnd.format('YYYY-MM-DD');
        }
        events.push(currentEvent as ParsedICalEvent);
      }
      currentEvent = null;
    } else if (inEvent && currentEvent) {
      // Parse SUMMARY (title)
      if (line.startsWith('SUMMARY')) {
        const value = extractICalValue(line);
        currentEvent.title = unescapeICalText(value);
      }
      // Parse DTSTART
      else if (line.startsWith('DTSTART')) {
        currentEvent.start = parseICalDate(line);
      }
      // Parse DTEND
      else if (line.startsWith('DTEND')) {
        currentEvent.end = parseICalDate(line);
        endIsExclusive = isDateOnlyProperty(line);
      }
      // Parse DESCRIPTION
      else if (line.startsWith('DESCRIPTION')) {
        const value = extractICalValue(line);
        currentEvent.description = unescapeICalText(value);
      }
    }
  }

  return events;
}

/**
 * Extracts the value from an iCal property line
 * Handles parameters like ;VALUE=DATE
 */
function extractICalValue(line: string): string {
  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) return '';
  return line.substring(colonIndex + 1).trim();
}

/**
 * Parses an iCal date string to ISO format (YYYY-MM-DD)
 * Handles formats like:
 * - DTSTART:20240101
 * - DTSTART;VALUE=DATE:20240101
 * - DTSTART:20240101T100000Z
 */
function parseICalDate(line: string): string {
  const value = extractICalValue(line);
  
  // Remove any 'Z' suffix and time component for date-only
  const dateStr = value.replace('Z', '').substring(0, 8);
  
  if (dateStr.length >= 8) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}`;
  }
  
  return value;
}

/**
 * True when the property carries a date-only (all-day) value, e.g.
 * `DTEND;VALUE=DATE:20270102` or `DTEND:20270102`.
 */
function isDateOnlyProperty(line: string): boolean {
  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) return false;
  const params = line.substring(0, colonIndex).toUpperCase();
  if (params.includes('VALUE=DATE-TIME')) return false;
  if (params.includes('VALUE=DATE')) return true;
  return /^\d{8}$/.test(extractICalValue(line));
}

/**
 * Unescapes iCal text (handles escaped characters)
 */
function unescapeICalText(text: string): string {
  return text
    .replace(/\\n/g, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

/**
 * Converts parsed iCal events to CreatePublicHolidayDto format
 * @param events - Parsed iCal events
 * @param bundesland - The bundesland code to assign
 * @param year - The year filter (only include events from this year)
 * @returns Array of CreatePublicHolidayDto
 */
export function convertToPublicHolidayDtos(
  events: ParsedICalEvent[],
  bundesland: string,
  year: number
): CreatePublicHolidayDto[] {
  return events
    .filter(event => {
      // Filter by year
      const eventYear = moment(event.start).year();
      return eventYear === year;
    })
    .map(event => ({
      title: event.title,
      start: event.start,
      end: event.end || event.start,
      bundesland,
      year,
    }));
}

/**
 * Reads an iCal file and returns parsed events
 * @param file - The File object from file input
 * @returns Promise resolving to parsed events
 */
export function readICalFile(file: File): Promise<ParsedICalEvent[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const events = parseICalContent(content);
        resolve(events);
      } catch (error) {
        reject(new Error('Fehler beim Parsen der iCal-Datei'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    reader.readAsText(file);
  });
}

/**
 * Validates that a file is an iCal file
 */
export function isValidICalFile(file: File): boolean {
  const validExtensions = ['.ics', '.ical', '.icalendar'];
  const fileName = file.name.toLowerCase();
  return validExtensions.some(ext => fileName.endsWith(ext));
}